import { useCallback, useState } from "react";
import { creatorListAPI } from "@/api/creatorList";
import { normalizeEmail } from "@/shared/utils/email";

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const useAssignmentContactEmail = (
	campaignId?: string,
	blockId?: string,
	onSaved?: (assignmentId: string, contactEmail: string | null) => void,
) => {
	const [drafts, setDrafts] = useState<Record<string, string>>({});
	const [errors, setErrors] = useState<Record<string, string>>({});
	const [saving, setSaving] = useState<Record<string, boolean>>({});

	const setDraft = useCallback((assignmentId: string, value: string) => {
		setDrafts((current) => ({ ...current, [assignmentId]: value }));
		setErrors((current) => ({ ...current, [assignmentId]: "" }));
	}, []);

	const save = useCallback(
		async (assignmentId: string, currentValue: string | null | undefined) => {
			if (!campaignId || !blockId) return false;
			const normalized = normalizeEmail(drafts[assignmentId] ?? currentValue);
			if (normalized && !EMAIL_PATTERN.test(normalized)) {
				setErrors((current) => ({ ...current, [assignmentId]: "Enter a valid email address" }));
				return false;
			}
			setSaving((current) => ({ ...current, [assignmentId]: true }));
			try {
				await creatorListAPI.updateAssignmentContactEmail(
					campaignId,
					blockId,
					assignmentId,
					normalized || null,
				);
				setDrafts((current) => ({ ...current, [assignmentId]: normalized }));
				setErrors((current) => ({ ...current, [assignmentId]: "" }));
				onSaved?.(assignmentId, normalized || null);
				return true;
			} catch (error: any) {
				setErrors((current) => ({
					...current,
					[assignmentId]: error?.response?.data?.error || "Unable to save email",
				}));
				return false;
			} finally {
				setSaving((current) => ({ ...current, [assignmentId]: false }));
			}
		},
		[blockId, campaignId, drafts, onSaved],
	);

	return { drafts, errors, saving, setDraft, save };
};
